import { on, fire } from "delegated-events";
import { gsap } from "gsap";
import ajax from "@common/ajax";
import scrollTo from "@common/scroll-to";
import { updateQueryParams } from "@common/util";
import Swiper from "swiper";
import { A11y, Autoplay, EffectFade, Pagination } from "swiper/modules";
import "@styles/components/slider.css";

let shouldScrollOnProgramChange = true;

const updateResults = async () => {
  const pageContent = document.querySelector("[data-page-content]");

  pageContent.classList.add("is-loading");

  const res = await ajax.get(window.location.href).json();

  pageContent.innerHTML = res.page;
  pageContent.classList.remove("is-loading");

  handleSelectedProgram();
};

on("change", ".program-location-filters input", (e) => {
  const location = e.currentTarget.value;
  const url = updateQueryParams([
    ["location", location === "__all__" ? false : location],
    ["program", false],
  ]);

  window.history.replaceState({}, "", url);

  updateResults();
});

on("click", `[data-change-view]:not([aria-selected="true"])`, (e) => {
  document.querySelector('[data-change-view][aria-selected="true"]').setAttribute("aria-selected", "false");
  e.currentTarget.setAttribute("aria-selected", "true");

  const url = updateQueryParams([["view", e.currentTarget.dataset.changeView]]);

  window.history.replaceState({}, "", url);

  updateResults();
});

on("programsearch:query", "html", (e) => {
  const url = updateQueryParams([
    ["search", e.detail.value],
    ["program", false],
    ["area", false],
    ["location", false],
  ]);

  window.history.replaceState({}, "", url);

  fire(document.documentElement, "programsearch:cleararea");

  updateResults();
});

on("programsearch:program", "html", (e) => {
  const url = updateQueryParams([
    ["program", e.detail.value],
    ["search", false],
    ["area", false],
    ["location", false],
  ]);

  window.history.replaceState({}, "", url);

  fire(document.documentElement, "programsearch:cleararea");

  shouldScrollOnProgramChange = true;

  updateResults();
});

on("programsearch:area", "html", (e) => {
  const url = updateQueryParams([
    ["area", e.detail.value],
    ["program", false],
    ["search", false],
    ["location", false],
  ]);

  window.history.replaceState({}, "", url);

  fire(document.documentElement, "programsearch:clearquery");

  updateResults();
});

on("click", ".clear-filters", () => {
  const url = updateQueryParams([
    ["search", false],
    ["program", false],
    ["area", false],
  ]);

  window.history.replaceState({}, "", url);

  fire(document.documentElement, "programsearch:clearall");

  updateResults();
});

const setupPhotoSlider = (item) => {
  const slider = item.querySelector(".program-photo-slider");

  if (!slider) {
    return;
  }

  new Swiper(slider.querySelector(".swiper"), {
    modules: [A11y, Autoplay, EffectFade, Pagination],
    autoplay: {
      delay: 3000,
    },
    effect: "fade",
    fadeEffect: {
      crossFade: true,
    },
    loop: true,
    speed: 1000,
    pagination: {
      el: slider.querySelector(".slider-pagination"),
      clickable: true,
    },
  });
};

const destroyPhotoSlider = (item) => {
  const slider = item.querySelector(".swiper");

  if (slider && slider.swiper) {
    slider.swiper.destroy();
  }
};

const handleSelectedProgram = () => {
  if (!shouldScrollOnProgramChange) {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  const program = params.get("program");

  if (!program) {
    return;
  }

  const programListEl = document.querySelector(`[data-list-program="${program}"]`);
  const programGridEl = document.querySelector(`[data-grid-program="${program}"]`);

  if (programListEl) {
    const item = document.getElementById(programListEl.getAttribute("aria-controls"));
    setupPhotoSlider(item);

    setTimeout(() => {
      scrollTo(programListEl, 32);
    }, 100);
  }

  if (programGridEl) {
    const grid = programGridEl.closest(".programs-grid");
    const expandedItem = document.getElementById(programGridEl.getAttribute("aria-controls"));
    const pos = parseInt(expandedItem.dataset.pos, 10);
    const numCols = parseInt(getComputedStyle(grid).getPropertyValue("--cols"), 10);
    const offset = pos % numCols;
    const order = offset === 0 ? pos - numCols : pos - offset;
    expandedItem.style.order = order;

    setupPhotoSlider(expandedItem);

    setTimeout(() => {
      scrollTo(expandedItem);
    }, 100);
  }

  shouldScrollOnProgramChange = false;
};

handleSelectedProgram();

const handleListOpen = (e) => {
  const trigger = e.detail.trigger;
  const item = document.getElementById(trigger.getAttribute("aria-controls"));

  if (item) {
    setupPhotoSlider(item);
  }

  scrollTo(trigger, 32);
};

const handleListClose = (e) => {
  const trigger = e.detail.trigger;
  const item = document.getElementById(trigger.getAttribute("aria-controls"));

  if (item) {
    destroyPhotoSlider(item);
  }

  const url = updateQueryParams([["program", false]]);

  window.history.replaceState({}, "", url);
};

const closeOtherGridItems = (grid) => {
  grid.querySelectorAll('.program-open-trigger[aria-expanded="true"]').forEach((trigger) => {
    const expandedItem = document.getElementById(trigger.getAttribute("aria-controls"));

    trigger.setAttribute("aria-expanded", "false");
    expandedItem.style.display = "none";
    destroyPhotoSlider(expandedItem);
  });
};

const handleGridOpen = async (e) => {
  const grid = e.target.closest(".programs-grid");
  const button = e.target.closest("button");
  const expandedItem = document.getElementById(button.getAttribute("aria-controls"));
  const pos = parseInt(expandedItem.dataset.pos, 10);
  const numCols = parseInt(getComputedStyle(grid).getPropertyValue("--cols"), 10);
  const offset = pos % numCols;
  const order = offset === 0 ? pos - numCols : pos - offset;

  closeOtherGridItems(grid);

  button.setAttribute("aria-expanded", "true");
  expandedItem.style.order = order;

  gsap.fromTo(
    expandedItem,
    { height: 0, opacity: 0, display: "block" },
    {
      height: "auto",
      opacity: 1,
      duration: 0.5,
      onComplete: () => {
        expandedItem.style.height = "auto";
        scrollTo(expandedItem);
        setupPhotoSlider(expandedItem);
      },
    },
  );
};

const handleGridClose = async (expandedItem) => {
  const item = document.querySelector(`[aria-controls="${expandedItem.id}"]`);
  item.setAttribute("aria-expanded", "false");

  gsap
    .timeline()
    .to(expandedItem, {
      opacity: 0,
      duration: 0.2,
    })
    .to(expandedItem, {
      height: 0,
      margin: 0,
      duration: 0.4,
      display: "none",
      onComplete: () => {
        expandedItem.style.margin = null;
        destroyPhotoSlider(expandedItem);
      },
    });

  const url = updateQueryParams([["program", false]]);

  window.history.replaceState({}, "", url);
};

on("click", ".program-open-trigger", (e) => {
  const button = e.target.closest("button");

  if (button.getAttribute("aria-expanded") === "true") {
    const target = button.getAttribute("aria-controls");
    const expandedItem = document.getElementById(target);

    handleGridClose(expandedItem);
  } else {
    handleGridOpen(e);
  }
});
on("click", ".program-close-trigger", (e) => {
  const expandedItem = e.target.closest(".program-expanded-item");

  handleGridClose(expandedItem);
});
on("slidetoggle:open", "html", handleListOpen);
on("slidetoggle:close", "html", handleListClose);
